import { com } from '@eidu/web'
import type { SnapshotEntity, GetEntities } from 'runtimes-shared'
import queryClient from '../../queryClient'
import getEntitiesOfType from './data/getEntitiesOfType'
import { getEntitiesFromResponse } from '../../../domain/entity/getEntitiesFromResponses'
import { entityKeys, entityTypeKeys } from '../../queryKeys'
import { fetchAllEntityTypes } from '../../useEntityTypes'
import entityTypeIdFromString = com.eidu.sharedlib.entity.type.entityTypeIdFromString
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import Entity = com.eidu.sharedlib.entity.Entity
import KeysetPaginatedResponseWithRelated = com.eidu.api.pagination.KeysetPaginatedResponseWithRelated

const PAGE_SIZE = 100
const DEFAULT_LIMIT = 200

// Keyset-paginate and stop as soon as we have `limit` entities. The previous indexed path fetched the
// entire type up front (one parallel request per page) just to fill a dropdown bounded by `limit`; a
// type with thousands of entities made that prohibitively expensive.
const fetchUpToLimit = async (
  typeId: string,
  id: EntityTypeId,
  limit: number,
  query: string | undefined,
  types: ReadonlyMap<EntityTypeId, EntityType | null>,
  kind: string
): Promise<SnapshotEntity[]> => {
  const results: SnapshotEntity[] = []
  let cursor: string | undefined = ''
  do {
    const response = await getEntitiesOfType({
      id,
      withRelatedEntities: true,
      pageSize: Math.min(PAGE_SIZE, limit - results.length),
      cursor,
      query,
    })
    if (!(response instanceof KeysetPaginatedResponseWithRelated))
      throw Error('Expected cursor-based pagination response from getEntitiesOfType')

    for (const [, entityWithLabel] of getEntitiesFromResponse(response, types)) {
      const entityId = entityWithLabel.entity.id.toString()
      // The serializer is the only way to get values in their JSON form; it omits empty maps.
      const json = JSON.parse(Entity.toJsonString(entityWithLabel.entity)) as Partial<
        Pick<SnapshotEntity, 'valuesByFieldId' | 'links'>
      >
      results.push({
        entityId,
        typeId,
        kind,
        // Fall back to the id like the field app does, so dropdowns never show blank options.
        primaryLabel: entityWithLabel.primaryLabelText || entityId,
        secondaryLabel: entityWithLabel.secondaryLabelText,
        valuesByFieldId: json.valuesByFieldId ?? {},
        links: json.links ?? {},
      })
      if (results.length >= limit) break
    }

    cursor = response.next || undefined
  } while (cursor && results.length < limit)

  return results
}

const getEntities: GetEntities = async (typeId, opts) => {
  const limit = opts?.limit ?? DEFAULT_LIMIT
  const query = opts?.query?.trim() || undefined
  const id = entityTypeIdFromString(typeId)

  const types = await queryClient.fetchQuery({ queryKey: entityTypeKeys.list(), queryFn: fetchAllEntityTypes })
  const kind = types.get(id)?.kind?.toString() ?? 'Generic'

  return queryClient.fetchQuery({
    queryKey: [...entityKeys.ofType(id), 'search', query ?? '', limit],
    queryFn: () => fetchUpToLimit(typeId, id, limit, query, types, kind),
  })
}

export default getEntities
